/**
 * Filters product gallery media by matching data-media-title and data-media-url against the selected color.
 * Used when the main product "Hide other variants' media" setting is enabled.
 */
(function () {
  const COLOR_OPTION_PATTERN = /^(color|colour)$/i;
  const COLOR_PART_SPLIT_PATTERN = /\s*(?:\/|&|,|\band\b)\s*/i;

  function normalizeColorLabel(colorLabel) {
    if (!colorLabel) return '';
    return colorLabel.split('--')[0].trim();
  }

  function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function compactColorString(value) {
    return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function splitColorParts(colorLabel) {
    const normalizedLabel = normalizeColorLabel(colorLabel);
    if (!normalizedLabel) return [];

    return normalizedLabel
      .split(COLOR_PART_SPLIT_PATTERN)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  function buildColorMatchers(colorLabel) {
    const parts = splitColorParts(colorLabel).map(compactColorString).filter(Boolean);
    if (!parts.length) return [];

    if (parts.length === 1) {
      return [new RegExp(escapeRegex(parts[0]), 'i')];
    }

    const combined = parts.join('');
    return [
      new RegExp(escapeRegex(combined), 'i'),
      new RegExp(parts.map(escapeRegex).join('[\\W_]*'), 'i'),
    ];
  }

  function getFilenameFromUrl(url) {
    if (!url) return '';

    try {
      const pathname = new URL(url, window.location.origin).pathname;
      return decodeURIComponent(pathname.split('/').pop() || '');
    } catch (error) {
      const withoutQuery = url.split('?')[0];
      return decodeURIComponent(withoutQuery.split('/').pop() || '');
    }
  }

  function getMediaSearchableText(mediaItem) {
    const title = mediaItem.dataset.mediaTitle || '';

    if (isVideoGalleryItem(mediaItem)) {
      return title;
    }

    const url = mediaItem.dataset.mediaUrl || '';
    const filename = getFilenameFromUrl(url);
    return [title, url, filename].filter(Boolean).join(' ');
  }

  function textMatchesColor(searchText, colorLabel) {
    const searchableValues = [searchText, compactColorString(searchText)];
    const matchers = buildColorMatchers(colorLabel);

    return matchers.some((matcher) => searchableValues.some((value) => matcher.test(value)));
  }

  function textMatchesAnyColor(searchText, colorLabels) {
    return colorLabels.some((colorLabel) => textMatchesColor(searchText, colorLabel));
  }

  function shouldShowMedia(searchText, selectedColor, allColors) {
    if (!textMatchesAnyColor(searchText, allColors)) return null;

    return textMatchesColor(searchText, selectedColor);
  }

  function isVideoGalleryItem(item) {
    return item.dataset.galleryVideo === 'true' || Boolean(
      item.querySelector('.thumbnail__badge .icon-play, .product__media-item--full deferred-media, video')
    );
  }

  function resolveItemVisibility(item, selectedColor, allColors) {
    const searchText = getMediaSearchableText(item);
    const colorMatch = shouldShowMedia(searchText, selectedColor, allColors);

    if (colorMatch !== null) {
      return colorMatch;
    }

    return true;
  }

  function readAllColors(variantSelects, mediaGallery) {
    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
    for (const fieldset of fieldsets) {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const firstInput = inputs[0];
      if (!firstInput || !isColorOptionName(getControlOptionName(firstInput))) continue;

      return inputs
        .map((input) => normalizeColorLabel(input.value || ''))
        .filter(Boolean);
    }

    const selects = Array.from(variantSelects.querySelectorAll('select'));
    for (const select of selects) {
      if (!isColorOptionName(getControlOptionName(select))) continue;

      return Array.from(select.options)
        .map((option) => normalizeColorLabel(option.value || ''))
        .filter(Boolean);
    }

    return (mediaGallery.dataset.colorOptions || '')
      .split('||')
      .map((color) => normalizeColorLabel(color))
      .filter(Boolean);
  }

  function isColorOptionName(optionName) {
    return COLOR_OPTION_PATTERN.test((optionName || '').trim());
  }

  function getControlOptionName(control) {
    const name = control?.getAttribute('name') || '';
    const optionMatch = name.match(/^options\[(.*)\]$/);
    return optionMatch ? optionMatch[1] : name;
  }

  function readSelectedColor(variantSelects) {
    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
    for (const fieldset of fieldsets) {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const firstInput = inputs[0];
      if (!firstInput || !isColorOptionName(getControlOptionName(firstInput))) continue;

      const checkedInput = inputs.find((input) => input.checked);
      return normalizeColorLabel(checkedInput?.value || '');
    }

    const selects = Array.from(variantSelects.querySelectorAll('select'));
    for (const select of selects) {
      if (!isColorOptionName(getControlOptionName(select))) continue;
      return normalizeColorLabel(select.value || '');
    }

    return null;
  }

  function getMediaImageKey(mediaItem) {
    const img = mediaItem.querySelector('img');
    if (!img) return '';

    const src = img.currentSrc || img.src || '';
    if (!src) return '';

    try {
      const { pathname } = new URL(src, window.location.origin);
      return decodeURIComponent(pathname).toLowerCase();
    } catch (error) {
      return src.split('?')[0].toLowerCase();
    }
  }

  function getViewerSlides(mediaGallery, sectionId) {
    const viewer = mediaGallery.querySelector(`#GalleryViewer-${sectionId}`);
    if (!viewer) return [];

    return Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]'));
  }

  function syncThumbnailVisibility(mediaGallery, sectionId) {
    const thumbnails = mediaGallery.querySelector(`#GalleryThumbnails-${sectionId}`);
    if (!thumbnails) return;

    thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
      const slide = mediaGallery.querySelector(
        `#GalleryViewer-${sectionId} [data-media-id="${thumbnail.dataset.target}"]`
      );
      if (!slide) return;

      const shouldHide = slide.hidden || slide.classList.contains('is-variant-color-filtered-out');
      setGalleryItemVisibility(thumbnail, shouldHide);
    });
  }

  function getGalleryItems(mediaGallery, sectionId) {
    const viewer = mediaGallery.querySelector(`#GalleryViewer-${sectionId}`);
    const thumbnails = mediaGallery.querySelector(`#GalleryThumbnails-${sectionId}`);
    const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
    const items = new Set();

    viewer?.querySelectorAll('[data-product-gallery-media]').forEach((item) => items.add(item));
    thumbnails?.querySelectorAll('[data-product-gallery-media]').forEach((item) => items.add(item));
    modalContent?.querySelectorAll('[data-media-id]').forEach((item) => items.add(item));

    return Array.from(items);
  }

  function setGalleryItemVisibility(item, shouldHide) {
    item.hidden = shouldHide;
    item.style.display = shouldHide ? 'none' : '';
    item.classList.toggle('is-variant-color-filtered-out', shouldHide);

    if (!shouldHide) return;

    item.classList.remove('is-active');
    item.querySelectorAll('deferred-media').forEach((media) => media.resetContent?.());
    const thumbnailButton = item.querySelector('button[aria-current]');
    if (thumbnailButton) thumbnailButton.removeAttribute('aria-current');
  }

  function getFirstVisibleMediaId(mediaGallery) {
    const visibleItem = mediaGallery.elements?.viewer?.querySelector(
      '[data-media-id]:not([hidden]):not(.is-variant-color-filtered-out)'
    );
    return visibleItem?.dataset.mediaId || null;
  }

  function reset(mediaGallery, sectionId) {
    getGalleryItems(mediaGallery, sectionId).forEach((item) => setGalleryItemVisibility(item, false));
    mediaGallery.classList.remove('media-gallery--color-filter-active');
    delete mediaGallery.dataset.activeGalleryColor;
    mediaGallery.elements?.viewer?.resetPages?.();
    mediaGallery.elements?.thumbnails?.resetPages?.();
    mediaGallery.syncThumbnailArrowVisibility?.();
  }

  function getVariantFeaturedMediaId(variant) {
    if (!variant) return null;
    return variant.featured_media?.id || variant.featured_image?.id || null;
  }

  function isStackedGallery(mediaGallery) {
    return mediaGallery.dataset.desktopLayout === 'stacked';
  }

  function resolveStackedFeaturedMediaId(variantSelects, mediaGallery) {
    if (variantSelects.currentVariant) {
      return getVariantFeaturedMediaId(variantSelects.currentVariant);
    }

    const mediaVariant =
      variantSelects.getMediaVariant?.({ allowWithoutHideVariants: true }) ||
      variantSelects.getFallbackMediaVariantForSelectedColor?.();

    if (mediaVariant) {
      return getVariantFeaturedMediaId(mediaVariant);
    }

    return mediaGallery.dataset.initialVariantMediaId || null;
  }

  function applyStackedVariantOnly(mediaGallery, sectionId, variantSelects) {
    const featuredMediaId = resolveStackedFeaturedMediaId(variantSelects, mediaGallery);
    const selectedMediaId = featuredMediaId ? `${sectionId}-${featuredMediaId}` : null;

    if (!selectedMediaId) {
      reset(mediaGallery, sectionId);
      return;
    }

    getViewerSlides(mediaGallery, sectionId).forEach((item) => {
      const shouldShow = Boolean(selectedMediaId && item.dataset.mediaId === selectedMediaId);
      setGalleryItemVisibility(item, !shouldShow);
    });

    syncThumbnailVisibility(mediaGallery, sectionId);

    const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
    modalContent?.querySelectorAll('[data-media-id]').forEach((item) => {
      const shouldShow = Boolean(featuredMediaId && String(item.dataset.mediaId) === String(featuredMediaId));
      setGalleryItemVisibility(item, !shouldShow);
    });

    mediaGallery.classList.add('media-gallery--color-filter-active');
    delete mediaGallery.dataset.activeGalleryColor;

    if (selectedMediaId && typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(selectedMediaId, false);
    }

    mediaGallery.elements?.viewer?.resetPages?.();
    mediaGallery.elements?.thumbnails?.resetPages?.();
    mediaGallery.syncThumbnailArrowVisibility?.();
  }

  function apply(mediaGallery, sectionId, { selectedColor, allColors }) {
    const seenImageKeys = new Set();
    const viewerSlides = getViewerSlides(mediaGallery, sectionId);
    let firstVisibleMediaId = null;

    viewerSlides.forEach((item) => {
      let shouldShow = resolveItemVisibility(item, selectedColor, allColors);

      if (shouldShow) {
        const imageKey = getMediaImageKey(item);
        if (imageKey) {
          if (seenImageKeys.has(imageKey)) {
            shouldShow = false;
          } else {
            seenImageKeys.add(imageKey);
          }
        }
      }

      setGalleryItemVisibility(item, !shouldShow);

      if (shouldShow && !firstVisibleMediaId && item.dataset.mediaId) {
        firstVisibleMediaId = item.dataset.mediaId;
      }
    });

    syncThumbnailVisibility(mediaGallery, sectionId);

    const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
    modalContent?.querySelectorAll('[data-media-id]').forEach((item) => {
      const shouldShow = resolveItemVisibility(item, selectedColor, allColors);
      setGalleryItemVisibility(item, !shouldShow);
    });

    mediaGallery.classList.add('media-gallery--color-filter-active');
    mediaGallery.dataset.activeGalleryColor = selectedColor;

    const activeMedia = mediaGallery.elements?.viewer?.querySelector('[data-media-id].is-active:not([hidden])');
    if (!activeMedia || activeMedia.hidden || activeMedia.classList.contains('is-variant-color-filtered-out')) {
      const nextMediaId = firstVisibleMediaId || getFirstVisibleMediaId(mediaGallery);
      if (nextMediaId && typeof mediaGallery.setActiveMedia === 'function') {
        mediaGallery.setActiveMedia(nextMediaId, false);
      }
    }

    mediaGallery.elements?.viewer?.resetPages?.();
    mediaGallery.elements?.thumbnails?.resetPages?.();
    mediaGallery.syncThumbnailArrowVisibility?.();
  }

  function syncFromVariantSelects(variantSelects, mediaGallery) {
    if (!variantSelects || !mediaGallery) return;

    const sectionId = variantSelects.dataset.section;
    const allColors = readAllColors(variantSelects, mediaGallery);

    if (isStackedGallery(mediaGallery)) {
      applyStackedVariantOnly(mediaGallery, sectionId, variantSelects);
      return;
    }

    if (!variantSelects.currentVariant) {
      reset(mediaGallery, sectionId);
      return;
    }

    const selectedColor = readSelectedColor(variantSelects);
    if (!selectedColor) {
      reset(mediaGallery, sectionId);
      return;
    }

    apply(mediaGallery, sectionId, { selectedColor, allColors });
  }

  window.VariantMediaFilter = {
    apply,
    reset,
    syncFromVariantSelects,
    // Exported for debugging and unit-style verification in the console.
    _internals: {
      buildColorMatchers,
      compactColorString,
      getMediaSearchableText,
      getMediaImageKey,
      resolveItemVisibility,
      isVideoGalleryItem,
      readAllColors,
      normalizeColorLabel,
      splitColorParts,
      textMatchesColor,
      textMatchesAnyColor,
    },
  };
})();
